import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';

const Analytics = () => {
  const [stats, setStats] = useState({
    overview: {
      total_conversations: 0,
      total_messages: 0,
      unique_customers: 0,
      ai_handled: 0,
      agent_handled: 0,
      average_resolution_time: 0,
      customer_satisfaction: 0,
      ai_accuracy: 0
    },
    by_language: {
      ar: 0,
      en: 0
    },
    by_status: {
      active: 0,
      resolved: 0,
      pending: 0
    },
    agent_performance: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('today');
  const navigate = useNavigate();

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/v1/dashboard/stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStats({
            overview: {
              total_conversations: data.stats?.total_conversations || 0,
              total_messages: data.stats?.total_messages || 0,
              unique_customers: data.stats?.unique_users || 0,
              ai_handled: data.stats?.ai_handled || 0,
              agent_handled: data.stats?.agent_handled || 0,
              average_resolution_time: data.stats?.avg_resolution_time || 0,
              customer_satisfaction: data.stats?.satisfaction_rate || 0,
              ai_accuracy: data.stats?.ai_accuracy || 0
            },
            by_language: data.stats?.by_language || { ar: 0, en: 0 },
            by_status: data.stats?.by_status || { active: 0, resolved: 0, pending: 0 },
            agent_performance: data.stats?.agent_performance || []
          });
          setError(null);
        }
      } else if (response.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        setError('Failed to load analytics');
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return '0s';
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return minutes > 0 ? `${minutes}m ${secs}s` : `${secs}s`;
  };

  const calculatePercentage = (value, total) => {
    if (!total) return 0;
    return ((value / total) * 100).toFixed(1);
  };

  return (
    <Layout>
      <div style={{ padding: '20px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '30px'
        }}>
          <div>
            <h1 style={{ margin: '0 0 10px 0' }}>Analytics Dashboard</h1>
            <p style={{ margin: 0, color: '#6c757d' }}>System performance and metrics overview</p>
          </div>
          <div>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              style={{
                padding: '10px 20px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
            </select>
          </div>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Loading analytics...</p>
          </div>
        )}

        {error && (
          <div style={{
            padding: '20px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '5px',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '20px',
              marginBottom: '30px'
            }}>
              <div style={{
                padding: '20px',
                backgroundColor: 'white',
                borderRadius: '10px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                borderLeft: '4px solid #007bff'
              }}>
                <div style={{ fontSize: '14px', color: '#6c757d', marginBottom: '5px' }}>
                  Total Conversations
                </div>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#007bff' }}>
                  {stats.overview.total_conversations}
                </div>
                <div style={{ fontSize: '12px', color: '#28a745', marginTop: '5px' }}>
                  {stats.overview.unique_customers} unique customers
                </div>
              </div>

              <div style={{
                padding: '20px',
                backgroundColor: 'white',
                borderRadius: '10px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                borderLeft: '4px solid #28a745'
              }}>
                <div style={{ fontSize: '14px', color: '#6c757d', marginBottom: '5px' }}>
                  Total Messages
                </div>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#28a745' }}>
                  {stats.overview.total_messages}
                </div>
                <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '5px' }}>
                  Across all conversations
                </div>
              </div>

              <div style={{
                padding: '20px',
                backgroundColor: 'white',
                borderRadius: '10px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                borderLeft: '4px solid #ffc107'
              }}>
                <div style={{ fontSize: '14px', color: '#6c757d', marginBottom: '5px' }}>
                  AI Accuracy
                </div>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#ffc107' }}>
                  {(stats.overview.ai_accuracy * 100).toFixed(0)}%
                </div>
                <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '5px' }}>
                  Customer satisfaction rate
                </div>
              </div>

              <div style={{
                padding: '20px',
                backgroundColor: 'white',
                borderRadius: '10px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                borderLeft: '4px solid #17a2b8'
              }}>
                <div style={{ fontSize: '14px', color: '#6c757d', marginBottom: '5px' }}>
                  Avg Resolution Time
                </div>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#17a2b8' }}>
                  {formatTime(stats.overview.average_resolution_time)}
                </div>
                <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '5px' }}>
                  Per conversation
                </div>
              </div>
            </div>

            <div style={{
              backgroundColor: 'white',
              borderRadius: '10px',
              padding: '20px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              marginBottom: '30px'
            }}>
              <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Conversation Handling</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
                <div>
                  <div style={{ marginBottom: '15px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '14px', color: '#6c757d' }}>AI Handled</span>
                      <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                        {stats.overview.ai_handled} ({calculatePercentage(stats.overview.ai_handled, stats.overview.total_conversations)}%)
                      </span>
                    </div>
                    <div style={{
                      height: '8px',
                      backgroundColor: '#e9ecef',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${calculatePercentage(stats.overview.ai_handled, stats.overview.total_conversations)}%`,
                        height: '100%',
                        backgroundColor: '#28a745',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  </div>

                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '14px', color: '#6c757d' }}>Agent Handled</span>
                      <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                        {stats.overview.agent_handled} ({calculatePercentage(stats.overview.agent_handled, stats.overview.total_conversations)}%)
                      </span>
                    </div>
                    <div style={{
                      height: '8px',
                      backgroundColor: '#e9ecef',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${calculatePercentage(stats.overview.agent_handled, stats.overview.total_conversations)}%`,
                        height: '100%',
                        backgroundColor: '#007bff',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: '16px' }}>By Status</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                      <span style={{ fontSize: '14px' }}>Active</span>
                      <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#ffc107' }}>
                        {stats.by_status.active}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                      <span style={{ fontSize: '14px' }}>Resolved</span>
                      <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#28a745' }}>
                        {stats.by_status.resolved}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                      <span style={{ fontSize: '14px' }}>Pending</span>
                      <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#dc3545' }}>
                        {stats.by_status.pending}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{
              backgroundColor: 'white',
              borderRadius: '10px',
              padding: '20px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Language Distribution</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div style={{
                  padding: '20px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '10px' }}>🇸🇦</div>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#007bff', marginBottom: '5px' }}>
                    {stats.by_language.ar}
                  </div>
                  <div style={{ fontSize: '14px', color: '#6c757d' }}>
                    Arabic ({calculatePercentage(stats.by_language.ar, stats.overview.total_conversations)}%)
                  </div>
                </div>

                <div style={{
                  padding: '20px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '10px' }}>🇬🇧</div>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#28a745', marginBottom: '5px' }}>
                    {stats.by_language.en}
                  </div>
                  <div style={{ fontSize: '14px', color: '#6c757d' }}>
                    English ({calculatePercentage(stats.by_language.en, stats.overview.total_conversations)}%)
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Analytics;
